import { ApexOptions } from 'apexcharts';
import { FC, useEffect, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';

export interface SparklineProps {
  /** Data points to display */
  data: number[];
  /** Line and gradient color (hex) */
  color: string;
  /** Chart height in pixels */
  height?: number;
  /** Series name for tooltip */
  name?: string;
  /** Custom tooltip value formatter */
  tooltipFormatter?: (val: number) => string;
  /** Labels for x-axis categories (e.g. date labels) */
  xAxisLabels?: string[];
  /** Y-axis minimum */
  yAxisMin?: number;
  /** Y-axis maximum */
  yAxisMax?: number;
}

const ANIMATION = {
  DELAY_MS: 100,
  SPEED_MS: 1500,
  GRADUAL_DELAY_MS: 50,
  DYNAMIC_SPEED_MS: 350,
} as const;

const GRADIENT = {
  shadeIntensity: 1,
  opacityFrom: 0.4,
  opacityTo: 0.1,
  stops: [0, 100] as const,
} as const;

const STROKE_WIDTH = 2;

const Sparkline: FC<SparklineProps> = ({
  data,
  color,
  height = 48,
  name = 'Value',
  tooltipFormatter,
  xAxisLabels,
  yAxisMin,
  yAxisMax,
}) => {
  // Delay rendering data to trigger draw animation
  const [animatedData, setAnimatedData] = useState<number[]>([]);

  useEffect(() => {
    if (data && data.length > 0) {
      const timer = setTimeout(() => setAnimatedData(data), ANIMATION.DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [data]);

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'area',
        fontFamily: 'Inter, "Noto Color Emoji", sans-serif',
        sparkline: { enabled: true },
        animations: {
          enabled: true,
          easing: 'linear',
          speed: ANIMATION.SPEED_MS,
          animateGradually: {
            enabled: true,
            delay: ANIMATION.GRADUAL_DELAY_MS,
          },
          dynamicAnimation: {
            enabled: true,
            speed: ANIMATION.DYNAMIC_SPEED_MS,
          },
        },
      },
      stroke: { curve: 'smooth', width: STROKE_WIDTH },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: GRADIENT.shadeIntensity,
          opacityFrom: GRADIENT.opacityFrom,
          opacityTo: GRADIENT.opacityTo,
          stops: [...GRADIENT.stops],
        },
      },
      colors: [color],
      tooltip: {
        enabled: true,
        fixed: { enabled: false },
        x: { show: !!xAxisLabels },
        y: tooltipFormatter ? { formatter: tooltipFormatter } : undefined,
        marker: { show: false },
      },
      ...(xAxisLabels ? { xaxis: { categories: xAxisLabels } } : {}),
      ...(yAxisMin != null || yAxisMax != null
        ? {
            yaxis: {
              ...(yAxisMin != null ? { min: yAxisMin } : {}),
              ...(yAxisMax != null ? { max: yAxisMax } : {}),
            },
          }
        : {}),
    }),
    [color, tooltipFormatter, xAxisLabels, yAxisMin, yAxisMax]
  );

  if (!data || data.length < 2) {
    return null;
  }

  return (
    <Chart
      type="area"
      series={[{ name, data: animatedData }]}
      options={options}
      height={height}
      width="100%"
    />
  );
};

export default Sparkline;
